import { fetchOpenAIText } from "../utils/openaiClient";

export function useCommunicativeSituationsGeneratorLogic({
  communicativeSituationsVocab, setCommunicativeSituationsVocab,
  communicativeSituationsTopic, setCommunicativeSituationsTopic,
  communicativeSituationsLevel, setCommunicativeSituationsLoading,
  generatedCommunicativeSituations, setGeneratedCommunicativeSituations,
  currentTab, onInsertToBoard, activeEditor,
  setShowCommunicativeSituationsGenerator, setSelectedTemplate,
}) {
  const handleCommunicativeSituationsGenerate = async () => {
    if (!communicativeSituationsVocab.trim()) return;

    setCommunicativeSituationsLoading(true);
    try {
      const content = await fetchOpenAIText({
        prompt: `Create a PROFESSIONAL-GRADE communicative situation exercise with these specifications:

              TARGET PARAMETERS:
              - Vocabulary: ${communicativeSituationsVocab}
              - Topic: ${communicativeSituationsTopic || 'general conversation'}
              - CEFR Level: ${communicativeSituationsLevel}

              STRICT LEVEL REQUIREMENTS (${communicativeSituationsLevel}):

              A1 (Beginner):
              - Grammar: ONLY present simple, present continuous, basic modals (can, must)
              - Vocabulary: 500-1000 most common words, concrete nouns, basic adjectives
              - Sentence length: 3-8 words maximum
              - Topics: personal info, family, daily routines, basic needs
              - Communication: simple questions/answers, basic requests, introductions

              A2 (Elementary):
              - Grammar: present/past simple, future with 'will', basic conditionals (if + present)
              - Vocabulary: 1000-2000 words, common collocations, basic phrasal verbs
              - Sentence length: 5-12 words
              - Topics: shopping, travel, work, hobbies, past experiences
              - Communication: describing experiences, making plans, giving simple advice

              B1 (Intermediate):
              - Grammar: all basic tenses, conditionals (1st, 2nd), passive voice, reported speech
              - Vocabulary: 2000-4000 words, idiomatic expressions, phrasal verbs
              - Sentence length: 8-18 words
              - Topics: opinions, problems, abstract concepts, hypothetical situations
              - Communication: expressing opinions, giving advice, discussing problems

              B2 (Upper-Intermediate):
              - Grammar: complex tenses, mixed conditionals, advanced passive, subjunctive
              - Vocabulary: 4000-6000 words, sophisticated collocations, formal/informal register
              - Sentence length: 12-25 words
              - Topics: complex social issues, professional contexts, abstract ideas
              - Communication: nuanced opinions, complex negotiations, detailed explanations

              C1 (Advanced):
              - Grammar: all advanced structures, complex conditionals, inversion, ellipsis
              - Vocabulary: 6000-8000 words, idiomatic language, cultural references
              - Sentence length: 15-30 words
              - Topics: specialized fields, complex social issues, abstract concepts
              - Communication: sophisticated argumentation, professional discourse

              C2 (Proficiency):
              - Grammar: native-like complexity, subtle nuances, advanced stylistic devices
              - Vocabulary: 8000+ words, highly idiomatic, cultural and literary references
              - Sentence length: 20-40 words
              - Topics: any topic with native-level sophistication
              - Communication: expert-level discourse, cultural awareness, subtle meanings

              EXERCISE REQUIREMENTS:

              1. DIALOGUE (12-15 lines):
              - Use ALL target vocabulary naturally and contextually
              - Create realistic, engaging conversation between 2-3 characters
              - Vary sentence structures according to level
              - Include natural conversation markers (well, actually, I mean, etc.)
              - Make it culturally appropriate and authentic
              - Format target vocabulary as **bold**

              2. ROLE PLAY SCENARIOS (2 scenarios):
              - Create detailed, realistic situations
              - Include specific context, setting, and character roles
              - Provide clear objectives for each role
              - Incorporate target vocabulary naturally
              - Make scenarios engaging and educational
              - Format target vocabulary as **bold**

              OUTPUT FORMAT (JSON):
              {
                "generalTopic": "2-5 word general topic inferred from the vocabulary and dialogue (e.g. 'job interviews', 'travelling abroad', 'making appointments')",
                "dialogue": "Character1: [natural line with **target vocab**]\\nCharacter2: [response with **target vocab**]\\nCharacter1: [follow-up with **target vocab**]\\n...",
                "roleplay1": "SITUATION: [detailed context]\\nROLES: [character descriptions]\\nOBJECTIVE: [what to achieve]\\nUse these words: **target vocab**",
                "roleplay2": "SITUATION: [detailed context]\\nROLES: [character descriptions]\\nOBJECTIVE: [what to achieve]\\nUse these words: **target vocab**"
              }

              QUALITY STANDARDS:
              - 100% authentic and natural language
              - Perfect level-appropriate complexity
              - Engaging and educational content
              - All target vocabulary used meaningfully
              - Realistic, practical scenarios
              - Clear learning objectives
              - Professional teaching quality

              CRITICAL: This exercise will be used in a professional language school. Quality must be exceptional.`,
        temperature: 0.7,
        maxTokens: 800,
      });

      if (content) {
        try {
          let cleanContent = content.trim();
          if (cleanContent.startsWith('```json')) {
            cleanContent = cleanContent.replace(/^```json\n?/, '').replace(/\n?```$/, '');
          } else if (cleanContent.startsWith('```')) {
            cleanContent = cleanContent.replace(/^```\n?/, '').replace(/\n?```$/, '');
          }
          const parsed = JSON.parse(cleanContent);
          setGeneratedCommunicativeSituations({
            dialogue: parsed.dialogue,
            roleplay1: parsed.roleplay1,
            roleplay2: parsed.roleplay2,
            generalTopic: parsed.generalTopic || ''
          });
        } catch (parseError) {
          setGeneratedCommunicativeSituations({
            dialogue: "Student: Can you help me with this vocabulary?\nTeacher: Of course! Let's practice together.",
            roleplay1: "Practice the target vocabulary in a store setting",
            roleplay2: "Use the vocabulary in a casual conversation"
          });
        }
      } else {
        setGeneratedCommunicativeSituations({
          dialogue: "Student: Can you help me with this vocabulary?\nTeacher: Of course! Let's practice together.",
          roleplay1: "Practice the target vocabulary in a store setting",
          roleplay2: "Use the vocabulary in a casual conversation"
        });
      }
    } catch (error) {
      setGeneratedCommunicativeSituations({
        dialogue: "Student: Can you help me with this vocabulary?\nTeacher: Of course! Let's practice together.",
        roleplay1: "Practice the target vocabulary in a store setting",
        roleplay2: "Use the vocabulary in a casual conversation"
      });
    } finally {
      setCommunicativeSituationsLoading(false);
    }
  };

  const handleCommunicativeSituationsInsert = () => {
    if (!generatedCommunicativeSituations) return;
    const situations = [
      { type: 'dialogue', html: (generatedCommunicativeSituations.dialogue || '').replace(/\n/g, '<br>') },
      { type: 'roleplay', text: generatedCommunicativeSituations.roleplay1 || '' },
      { type: 'roleplay', text: generatedCommunicativeSituations.roleplay2 || '' }
    ];
    const boardData = {
      title: 'Communicative Situation',
      level: communicativeSituationsLevel,
      topic: communicativeSituationsTopic || generatedCommunicativeSituations.generalTopic || '',
      vocab: communicativeSituationsVocab,
      situations,
    };
    if (currentTab > 0 && onInsertToBoard) {
      onInsertToBoard(boardData, 'communicativesituations');
      setShowCommunicativeSituationsGenerator(false);
      setCommunicativeSituationsVocab("");
      setCommunicativeSituationsTopic("");
      setGeneratedCommunicativeSituations(null);
      setSelectedTemplate(null);
      return;
    }
    if (activeEditor) {
      activeEditor.chain().focus().insertContent({
        type: 'communicativeSituations',
        attrs: {
          title: 'Communicative Situation',
          level: communicativeSituationsLevel,
          topic: communicativeSituationsTopic || generatedCommunicativeSituations.generalTopic || '',
          vocab: communicativeSituationsVocab,
          situations,
        }
      }).run();
    }
    setShowCommunicativeSituationsGenerator(false);
    setCommunicativeSituationsVocab("");
    setCommunicativeSituationsTopic("");
    setGeneratedCommunicativeSituations(null);
    setSelectedTemplate(null);
  };

  const handleCommunicativeSituationsCancel = () => {
    setShowCommunicativeSituationsGenerator(false);
    setCommunicativeSituationsVocab("");
    setCommunicativeSituationsTopic("");
    setGeneratedCommunicativeSituations(null);
    setSelectedTemplate(null);
  };

  return { handleCommunicativeSituationsGenerate, handleCommunicativeSituationsInsert, handleCommunicativeSituationsCancel };
}
